'use client'

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, Clock } from 'lucide-react'
import {
  Box,
  Card,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { PageLoader } from '@/components/ui/page-loader'
import { Money, FinancialOnly } from '@crm-src/components/money'
import { invoiceSchedulesApi, formatFrequency } from '@crm-src/lib/invoice-schedules-api'
import type { ProjectedOccurrence } from '@crm-src/types/invoice-schedule'
import {
  groupByMonth,
  dayOfMonth,
  monthAbbrUpper,
  formatDateShort,
  relativeDayLabel,
  daysUntil,
} from './recurring-timeline-utils'

// Horizon presets for the timeline range selector. Values are the `days`
// window the projection endpoint clamps to (max 366).
const HORIZONS: { value: number; label: string }[] = [
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '6 months' },
  { value: 365, label: '12 months' },
]

// An occurrence within a week reads as "due soon" — accent its rail dot + label.
const SOON_DAYS = 7

interface RecurringTimelineProps {
  /** Restrict to a single customer's schedules (mirrors the ?customer_id filter
   *  on the parent page). */
  customerId?: string | null
  /** Bump to force a re-fetch of the projection — e.g. after the parent page
   *  creates a new schedule, so its upcoming invoices appear immediately. */
  refreshSignal?: number
}

/**
 * Forward-looking timeline of the invoices every ACTIVE recurring schedule is
 * projected to generate, merged across schedules and grouped by month. Reads
 * GET /invoice_schedules/projection (one call) and renders each projected
 * occurrence as a card on a vertical rail. Amounts route through <Money> so the
 * financial wall redacts them for a viewer without financial access.
 */
export function RecurringTimeline({ customerId, refreshSignal = 0 }: RecurringTimelineProps) {
  const navigate = useNavigate()
  const [days, setDays] = useState(90)
  const [occurrences, setOccurrences] = useState<ProjectedOccurrence[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const fetchProjection = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await invoiceSchedulesApi.projection({
        days,
        ...(customerId ? { customer_id: customerId } : {}),
      })
      setOccurrences(res.success && res.data ? res.data.occurrences : [])
    } finally {
      setIsLoading(false)
    }
  }, [days, customerId])

  useEffect(() => {
    // fetchProjection already depends on days + customerId; refreshSignal is an
    // extra, explicit re-fetch trigger from the parent page (e.g. after create).
    fetchProjection()
  }, [fetchProjection, refreshSignal])

  const groups = useMemo(() => groupByMonth(occurrences), [occurrences])
  const count30 = useMemo(
    () => occurrences.filter((o) => daysUntil(o.scheduled_date) <= 30).length,
    [occurrences],
  )
  const projectedValue = useMemo(
    () => occurrences.reduce((sum, o) => sum + (o.amount ?? 0), 0),
    [occurrences],
  )

  const horizonSelect = (
    <TextField
      select
      size="small"
      label="Range"
      value={days}
      onChange={(e) => setDays(Number(e.target.value))}
      sx={{ width: 150 }}
      inputProps={{ 'data-testid': 'timeline-horizon' }}
    >
      {HORIZONS.map((h) => (
        <MenuItem key={h.value} value={h.value}>
          {h.label}
        </MenuItem>
      ))}
    </TextField>
  )

  return (
    <Box data-testid="recurring-timeline" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header + range selector */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 600 }}>Upcoming invoices</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Projected across all active schedules — when each invoice is expected to generate.
          </Typography>
        </Box>
        {horizonSelect}
      </Box>

      {/* Summary tiles */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
        <SummaryTile label="Next 30 days" value={`${count30}`} />
        <SummaryTile label={`Projected invoices (${days}d)`} value={`${occurrences.length}`} />
        <FinancialOnly>
          <SummaryTile label={`Projected value (${days}d)`} value={<Money value={projectedValue} />} />
        </FinancialOnly>
      </Box>

      {isLoading ? (
        <PageLoader />
      ) : occurrences.length === 0 ? (
        <Box data-testid="timeline-empty" sx={{ textAlign: 'center', py: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', color: 'text.disabled' }}>
            <Calendar size={48} />
          </Box>
          <Typography component="h3" sx={{ mt: 2, fontSize: '1.125rem', fontWeight: 500 }}>
            No upcoming invoices
          </Typography>
          <Typography sx={{ mt: 1, color: 'text.secondary', maxWidth: 448, mx: 'auto' }}>
            No active schedule is projected to generate an invoice in the next {days} days.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', pl: 3 }}>
          {/* The vertical rail behind the occurrence dots. */}
          <Box
            sx={{
              position: 'absolute',
              left: 6,
              top: 8,
              bottom: 8,
              width: '2px',
              bgcolor: 'divider',
            }}
          />

          {groups.map((group) => (
            <Box key={group.key}>
              <Typography
                sx={{
                  ml: -3,
                  pl: 3,
                  mt: 2,
                  mb: 1.5,
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'text.disabled',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {group.label}
              </Typography>

              {group.items.map((occ) => {
                const soon = daysUntil(occ.scheduled_date) <= SOON_DAYS
                const customerName =
                  occ.customer?.company_name || occ.customer?.full_name || 'Unknown customer'
                return (
                  <Box
                    key={`${occ.schedule_id}-${occ.occurrence_number}`}
                    sx={{ position: 'relative', mb: 1.5 }}
                  >
                    {/* Rail dot */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: -18,
                        top: 18,
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: 'background.paper',
                        border: 2,
                        borderColor: soon ? 'primary.main' : 'divider',
                      }}
                    />
                    <Card
                      variant="outlined"
                      data-testid="timeline-occurrence"
                      onClick={() => navigate(`/crm/invoices/schedules/${occ.schedule_id}`)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'border-color 120ms ease',
                        '&:hover': { borderColor: 'text.secondary' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5 }}>
                        {/* Date badge */}
                        <Box sx={{ textAlign: 'center', minWidth: 44 }}>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                            {monthAbbrUpper(occ.scheduled_date)}
                          </Typography>
                          <Typography sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1 }}>
                            {dayOfMonth(occ.scheduled_date)}
                          </Typography>
                        </Box>

                        {/* Schedule + customer + cadence */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {occ.schedule_name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: 'text.secondary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {customerName} · {formatFrequency(occ.frequency, occ.interval)} · due{' '}
                            {formatDateShort(occ.due_date)}
                          </Typography>
                        </Box>

                        {/* Amount + relative timing */}
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography sx={{ fontWeight: 600 }}>
                            <Money value={occ.amount} />
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: soon ? 'primary.main' : 'text.disabled',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              justifyContent: 'flex-end',
                            }}
                          >
                            <Clock size={12} />
                            {relativeDayLabel(occ.scheduled_date)}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Box>
                )
              })}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

/** Small surface tile used for the timeline's summary counts. */
function SummaryTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: 'background.paper' }}>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 600, mt: 0.5 }}>{value}</Typography>
    </Box>
  )
}

export default RecurringTimeline
